import { useMemo } from "react";
import useSWR from "swr";
import type { User } from "@/lib/swr";

// User directory (/users, public shape: id/name/avatar/email/roles). PRD,
// engagement and version payloads carry people as id references only (ownerId,
// memberIds, followerIds…) — resolve them here. One SWR key, so every
// component takes its own copy and dedups automatically (no prop drilling).
export const useUsers = (): User[] => {
  const { data } = useSWR<User[]>(["/users"]);
  return data || [];
};

export const useUserMap = (): Map<string, User> => {
  const users = useUsers();
  return useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
};

// One user by id; undefined while the directory loads or for unknown ids
// (deleted users drop out of /users).
export const useUser = (id?: string | null): User | undefined => {
  const map = useUserMap();
  return id ? map.get(id) : undefined;
};

// Resolve an id list, keeping order and dropping unknown ids.
export const useUsersByIds = (ids?: string[] | null): User[] => {
  const map = useUserMap();
  return useMemo(
    () =>
      (ids ?? [])
        .map((id) => map.get(id))
        .filter((u): u is User => !!u),
    [ids, map]
  );
};
